import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useEffect } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { Toaster } from 'sonner';
import Layout from './components/Layout';
import InstallPWA from './components/InstallPWA';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import StudentsPage from './pages/StudentsPage';
import StudentFormPage from './pages/StudentFormPage';
import StudentDetailPage from './pages/StudentDetailPage';
import TeachersPage from './pages/TeachersPage';
import QuranProgressPage from './pages/QuranProgressPage';
import EvaluationsPage from './pages/EvaluationsPage';
import GradesPage from './pages/GradesPage';
import BulletinsPage from './pages/BulletinsPage';
import ClassesPage from './pages/ClassesPage';
import AttendancePage from './pages/AttendancePage';
import ParentPortalPage from './pages/ParentPortalPage';
import PaymentsPage from './pages/PaymentsPage';
import SchoolYearsPage from './pages/SchoolYearsPage';
import SettingsPage from './pages/SettingsPage';
import StudentsHistoryPage from './pages/StudentsHistoryPage';
// Internat pages
import InternatDashboardPage from './pages/InternatDashboardPage';
import HebergementPage from './pages/HebergementPage';
import PermissionsPage from './pages/PermissionsPage';
import RestaurationPage from './pages/RestaurationPage';
import BoardingPresencePage from './pages/BoardingPresencePage';
import MedicalPage from './pages/MedicalPage';
import BoardingFeesPage from './pages/BoardingFeesPage';
import MobilePaymentsPage from './pages/MobilePaymentsPage';
import QuranicDailyEvalPage from './pages/QuranicDailyEvalPage';
import MonthlyEvalPage from './pages/MonthlyEvalPage';
import TrimestrialEvalPage from './pages/TrimestrialEvalPage';
import EvalDashboardPage from './pages/EvalDashboardPage';
import StudentQuranProfilePage from './pages/StudentQuranProfilePage';
// Admin pages
import RolesPermissionsPage from './pages/RolesPermissionsPage';
import UsersManagementPage from './pages/UsersManagementPage';
// Finance pages
import ExpensesPage from './pages/ExpensesPage';
import SalarySlipsPage from './pages/SalarySlipsPage';
import ExpenseCategoriesPage from './pages/ExpenseCategoriesPage';
import { register as registerServiceWorker } from './serviceWorkerRegistration';
import './App.css';

// Register service worker
registerServiceWorker();

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center animate-pulse">
            <svg
              className="w-6 h-6 text-primary"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
              />
            </svg>
          </div>
          <p className="text-muted-foreground">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <Layout>{children}</Layout>;
};

// Public Route wrapper (redirects to dashboard if logged in)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center animate-pulse">
          <svg
            className="w-6 h-6 text-primary"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
            />
          </svg>
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />

      {/* Protected routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/students"
        element={
          <ProtectedRoute>
            <StudentsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/students/new"
        element={
          <ProtectedRoute>
            <StudentFormPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/students/:id"
        element={
          <ProtectedRoute>
            <StudentDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/students/:id/edit"
        element={
          <ProtectedRoute>
            <StudentFormPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/students-history"
        element={
          <ProtectedRoute>
            <StudentsHistoryPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/teachers"
        element={
          <ProtectedRoute>
            <TeachersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/quran-progress"
        element={
          <ProtectedRoute>
            <QuranProgressPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/student-quran/:id"
        element={
          <ProtectedRoute>
            <StudentQuranProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/quranic-daily-eval"
        element={
          <ProtectedRoute>
            <QuranicDailyEvalPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/monthly-eval"
        element={
          <ProtectedRoute>
            <MonthlyEvalPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/trimestrial-eval"
        element={
          <ProtectedRoute>
            <TrimestrialEvalPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/eval-dashboard"
        element={
          <ProtectedRoute>
            <EvalDashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/evaluations"
        element={
          <ProtectedRoute>
            <EvaluationsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/grades"
        element={
          <ProtectedRoute>
            <GradesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/bulletins"
        element={
          <ProtectedRoute>
            <BulletinsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/classes"
        element={
          <ProtectedRoute>
            <ClassesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/attendance"
        element={
          <ProtectedRoute>
            <AttendancePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/parent-portal"
        element={
          <ProtectedRoute>
            <ParentPortalPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/payments"
        element={
          <ProtectedRoute>
            <PaymentsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/mobile-payments"
        element={
          <ProtectedRoute>
            <MobilePaymentsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/boarding-fees"
        element={
          <ProtectedRoute>
            <BoardingFeesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/school-years"
        element={
          <ProtectedRoute>
            <SchoolYearsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        }
      />
      {/* Internat routes */}
      <Route
        path="/internat"
        element={
          <ProtectedRoute>
            <InternatDashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/internat/hebergement"
        element={
          <ProtectedRoute>
            <HebergementPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/internat/permissions"
        element={
          <ProtectedRoute>
            <PermissionsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/internat/restauration"
        element={
          <ProtectedRoute>
            <RestaurationPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/internat/presence"
        element={
          <ProtectedRoute>
            <BoardingPresencePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/internat/medical"
        element={
          <ProtectedRoute>
            <MedicalPage />
          </ProtectedRoute>
        }
      />
      {/* Admin routes */}
      <Route
        path="/admin/roles"
        element={
          <ProtectedRoute>
            <RolesPermissionsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute>
            <UsersManagementPage />
          </ProtectedRoute>
        }
      />

      {/* Finance */}
      <Route
        path="/expenses"
        element={
          <ProtectedRoute>
            <ExpensesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/salary-slips"
        element={
          <ProtectedRoute>
            <SalarySlipsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/expense-categories"
        element={
          <ProtectedRoute>
            <ExpenseCategoriesPage />
          </ProtectedRoute>
        }
      />

      {/* Catch all - redirect to dashboard */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <AuthProvider>
          <AppRoutes />
          <InstallPWA />
          <Toaster
            position="top-right"
            richColors
            closeButton
            toastOptions={{
              style: {
                borderRadius: '12px',
              },
            }}
          />
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
